import React, { useMemo } from 'react';
import { Alert, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { TimeReport } from '../../api/TimeReportsApiClient';

const { Text } = Typography;

interface Props {
  selectedDate: Dayjs;
  timeReports: TimeReport[] | { log: TimeReport; payment: number }[];
  userId: string;
}

const CPEWarning: React.FC<Props> = ({ selectedDate, timeReports, userId }) => {
  const { t } = useTranslation();

  const cpeLogsForDay = useMemo(() => {
    if (!selectedDate || !timeReports?.length) return [];
    const day = selectedDate.format('YYYY-MM-DD');
    return timeReports
      .map(item => ('log' in item ? item.log : item) as TimeReport)
      .filter(r => {
        return r.userId === userId &&
        dayjs(r.endTime)?.format('YYYY-MM-DD') === day &&
        r.description?.toLowerCase().includes('cpe');
      });
  }, [selectedDate, timeReports, userId]);

  if (!cpeLogsForDay.length) return null;

  const totalHours = cpeLogsForDay.reduce((sum, r) => {
    const [h = '0', m = '0'] = (r.totalTime ?? '').split(':');
    return sum + Number(h) + Number(m) / 60;
  }, 0);

  const fmt = (hrs: number) => {
    const h = Math.floor(hrs);
    const m = Math.round((hrs - h) * 60);
    return `${h}:${m.toString().padStart(2, '0')}`;
  };

  return (
    <Alert
      type="info"
      showIcon
      icon={<InfoCircleOutlined />}
      style={{ marginBottom: 16 }}
      message={t('CPE Time Already Logged')}
      description={
        <>
          <Text>{t('Automatic time logging detected for calendar events on this day.')}</Text>
          <br />
          <Text strong>
            {t('Total CPE time logged')}: {fmt(totalHours)}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {cpeLogsForDay.map((r, i) => (
              <div key={i}>• {r.description} ({r.totalTime})</div>
            ))}
          </Text>
        </>
      }
    />
  );
};

export default CPEWarning;
